import { NgClass } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    signal,
    ViewEncapsulation,
} from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthService, CKAuthService } from '@ck-core/ck-auth';
import { TranslocoDirective } from '@jsverse/transloco';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { catchError, map, of } from 'rxjs';
import { FeedbackPopupComponent } from '../../feedback-popup/feedback-popup.component';
import { SvgIcon } from '../svg-icon/svg-icon';

import {environment} from '../../../../environments/environment';
import {PageHeaderService} from '../../../core/services/page-header.service';
import {ConfirmService} from '../../confirm/confirm.service';
import {HEADER_NAV, HEADER_USER_MENU, HeaderUserMenuItem, HeaderNavItem} from './header.config';
import {UserRoleEnum} from '../../../models/users/user.model';

@Component({
    selector: 'app-header',
    imports: [
        NgClass,
        RouterLink,
        TranslocoDirective,
        NgbDropdownModule,
        SvgIcon,
    ],
    templateUrl: './header.html',
    styleUrl: './header.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class Header {
    private readonly router = inject(Router);
    private readonly pageHeader = inject(PageHeaderService);
    private readonly auth = inject(CKAuthService);
    private readonly authHttp = inject(AuthService);
    private readonly confirm = inject(ConfirmService);
    private readonly modal = inject(NgbModal);

    protected readonly navItems = HEADER_NAV;
    protected readonly userMenu = computed(() => {
        const rawRole = this.auth.authData?.role;
        const roles = getRolesList(rawRole);
        const isExecutiveAdmin =
            roles.includes(UserRoleEnum.ExecutiveAdmin) || roles.includes('Executive Admin') ||
            roles.includes(UserRoleEnum.SaasAdmin) || roles.includes('Saas Admin');
        const isOperationalAdmin =
            roles.includes(UserRoleEnum.OperationalAdmin) || roles.includes('Operational Admin') ||
            roles.includes(UserRoleEnum.SaasAdmin) || roles.includes('Saas Admin');

        return HEADER_USER_MENU.filter((item) => {
            if (item.id === 'users') {
                return isExecutiveAdmin;
            }
            if (item.id === 'settings') {
                return isExecutiveAdmin || isOperationalAdmin;
            }
            return true;
        });
    });

    /** Destination for clicking the brand logo — taken from env so it can
     *  be tweaked per deployment. Falls back to `/` for safety. */
    protected readonly homeRoute: string = environment.homeRoute || '/';

    /** AST product branding — never the customer's uploaded company logo. */
    protected readonly brandLogoUrl = environment.defaultLogoUrl;

    protected get userName(): string {
        const authName = this.auth.authData?.name;
        if (authName && authName.trim() !== '') {
            return authName.trim();
        }

        const firstName = this.auth.authData?.moreInfo?.firstName || '';
        const lastName = this.auth.authData?.moreInfo?.lastName || '';
        const fullName = `${firstName} ${lastName}`.trim();

        if (fullName) {
            return fullName;
        }

        return (this.auth.authData?.moreInfo?.email || '').trim();
    }

    protected get userInitials(): string {
        return this.computeInitials(this.userName) || 'U';
    }

    protected get userEmail(): string | undefined {
        return this.auth.authData?.moreInfo?.email;
    }

    protected get isMimic(): boolean {
        return this.auth.authData?.moreInfo?.isMimic ?? false;
    }

    protected isActiveItem(item: HeaderNavItem): boolean {
        if (!item.activeRoutes) return false;
        const currentUrl = this.router.url.split('?')[0];
        return item.activeRoutes.some((r: string) => currentUrl.startsWith(r));
    }

    private readonly signingOut = signal(false);

    protected readonly mode = computed(() => this.pageHeader.config().mode);

    protected triggerAction(action: 'inviteUser' | 'createNewBlank' | 'importBlank' | 'importSettings' | 'exportSettings'): void {
        this.pageHeader.triggerAction(action);
    }

    protected goBack(): void {
        this.router.navigateByUrl('/');
    }

    protected openFeedback(): void {
        this.modal.open(FeedbackPopupComponent, { centered: true, size: 'md' });
    }

    protected onUserMenuClick(item: HeaderUserMenuItem): void {
        switch (item.action.kind) {
            case 'route':
                this.router.navigateByUrl(item.action.route);
                return;
            case 'sign-out':
                this.signOut();
                return;
            case 'noop':
                // Intentional placeholder — the destination screen isn't
                // wired up yet. The dropdown will close on its own.
                return;
        }
    }

    private signOut(): void {
        if (this.signingOut()) return;

        const tenantId = this.auth.authData?.moreInfo?.tenantId;

        this.confirm
            .ask({
                title: 'header.signOutConfirm.title',
                message: 'header.signOutConfirm.message',
                confirmLabel: 'header.signOutConfirm.confirm',
                cancelLabel: 'header.signOutConfirm.cancel',
                variant: 'danger',
                icon: 'warning',
                confirmAction: () => {
                    this.signingOut.set(true);
                    return this.authHttp.Logout(tenantId).pipe(
                        map(() => true as const),
                        catchError(() => of(true as const)),
                    );
                },
            })
            .then(() => this.forceSignOut())
            .catch(() => {
                this.signingOut.set(false);
            });
    }

    private forceSignOut(): void {
        this.auth.Logout();
        localStorage.clear();
        window.location.href = environment.loginRoute;
    }

    private computeInitials(name: string): string {
        const cleaned = (name ?? '').trim();
        if (!cleaned) return '';
        const parts = cleaned.split(/\s+/).filter(Boolean);
        if (parts.length === 1) {
            return parts[0].substring(0, 2).toUpperCase();
        }
        const first = parts[0]?.[0] ?? '';
        const last = parts[parts.length - 1]?.[0] ?? '';
        return `${first}${last}`.toUpperCase();
    }
}

export function getRolesList(rawRole: any): string[] {
    if (!rawRole) return [];
    const list = Array.isArray(rawRole) ? rawRole : [rawRole];
    const roles: string[] = [];
    for (const r of list) {
        if (!r) continue;
        if (typeof r === 'string') {
            roles.push(r);
        } else if (typeof r === 'object') {
            if (r.id) roles.push(String(r.id));
            if (r.name) roles.push(String(r.name));
        }
    }
    return roles;
}
